import { useEffect, useRef, useState } from 'react'
import {
  CheckCircle2, Camera, Loader2, Trash2, Plus, X, ImageOff, AlertTriangle,
} from 'lucide-react'
import { patchJson } from '../../lib/api.js'
import { generateThumbnail } from '../../lib/thumbnail.js'
import { uploadFile } from '../../lib/uploadDirect.js'
import { fmtDateTH, realChecks } from '../../lib/asset.js'
import ImageLightbox from './ImageLightbox.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const MAX_PHOTOS = 4

function blobToDataURL(blob) {
  return new Promise((resolve) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = () => resolve('')
    r.readAsDataURL(blob)
  })
}

function fmtTime(ms) {
  if (!ms) return ''
  const d = typeof ms === 'number' ? new Date(ms) : new Date(ms)
  if (isNaN(d)) return ''
  const bkk = new Date(d.getTime())
  return bkk.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
}

/* One photo tile: prefers the inline thumbnail, falls back to the proxied full
 * image; clicking opens the full-res file (presigned redirect) in a new tab. */
function PhotoTile({ file, onClick }) {
  const src = file.thumbnail || (file.location ? `/api/asset-file/${file.location.split('/').map(encodeURIComponent).join('/')}` : '')
  if (!src) return <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300"><ImageOff size={16} /></div>
  return (
    <button type="button" onClick={onClick} className="block w-16 h-16 rounded-lg overflow-hidden ring-1 ring-slate-200 hover:ring-brand-300">
      <img src={src} alt={file.name || 'photo'} className="w-full h-full object-cover" />
    </button>
  )
}

export default function AssetChecks({ asset, onChanged, autoOpen = false }) {
  const checks = realChecks(asset).slice().sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
  const [adding, setAdding] = useState(false)
  const [remark, setRemark] = useState('')
  const [picked, setPicked] = useState([]) // { file, preview }
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [busyIdx, setBusyIdx] = useState(-1)
  const [lb, setLb] = useState(null)
  const [confirmEntry, setConfirmEntry] = useState(null)
  const fileRef = useRef(null)

  // Arriving from a scanned sticker (?u= on the detail page) means the user is
  // standing in front of the machine — open the form for them.
  useEffect(() => { if (autoOpen) setAdding(true) }, [autoOpen])

  function onPick(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setPicked((p) => {
      const next = [...p]
      for (const f of files) {
        if (next.length >= MAX_PHOTOS) break
        next.push({ file: f, preview: URL.createObjectURL(f) })
      }
      return next
    })
    e.target.value = ''
  }

  function removePicked(i) {
    setPicked((p) => {
      const t = p.slice()
      const [rm] = t.splice(i, 1)
      if (rm?.preview) URL.revokeObjectURL(rm.preview)
      return t
    })
  }

  function resetForm() {
    picked.forEach((p) => p.preview && URL.revokeObjectURL(p.preview))
    setPicked([]); setRemark(''); setErr(''); setAdding(false)
  }

  async function save() {
    if (!picked.length && !remark.trim()) { setErr('ถ่ายรูปหรือกรอกหมายเหตุอย่างน้อยหนึ่งอย่าง'); return }
    setSaving(true); setErr('')
    try {
      const fileObjs = []
      for (const { file } of picked) {
        // Direct to Wasabi where CORS allows, else the old proxy — uploadDirect.js.
        const obj = await uploadFile('/api/asset-upload', { asset_id: String(asset.id) }, file)
        const thumb = await generateThumbnail(file).catch(() => null)
        if (thumb) obj.thumbnail = await blobToDataURL(thumb)
        fileObjs.push(obj)
      }
      const newEntry = { remark: remark.trim(), file: fileObjs, created_at: Date.now() }
      const next = [...(Array.isArray(asset.check) ? asset.check : []), newEntry]
      await patchJson(`/api/assets/${asset.id}`, { check: next })
      resetForm()
      onChanged?.()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setSaving(false)
    }
  }

  async function removeCheck(entry) {
    setConfirmEntry(null)
    // find its index in the RAW array (checks[] is a filtered+sorted view)
    const raw = Array.isArray(asset.check) ? asset.check : []
    const idx = raw.indexOf(entry)
    if (idx < 0) return
    setBusyIdx(idx); setErr('')
    try {
      // best-effort remove the Wasabi objects
      for (const f of entry.file || []) {
        if (f.location) fetch(`/api/asset-file/${f.location.split('/').map(encodeURIComponent).join('/')}`, { method: 'DELETE', credentials: 'include' }).catch(() => {})
      }
      const next = raw.filter((_, i) => i !== idx)
      await patchJson(`/api/assets/${asset.id}`, { check: next })
      onChanged?.()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setBusyIdx(-1)
    }
  }

  return (
    <section id="asset-checks" className="rounded-2xl border border-slate-200 bg-white p-5 scroll-mt-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><CheckCircle2 size={16} strokeWidth={2} /></span>
          <h3 className="text-[14.5px] font-semibold text-slate-800">การตรวจเช็กตามรอบ <span className="text-slate-400 font-normal">({checks.length})</span></h3>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100">
            <Plus size={14} /> บันทึกการตรวจ
          </button>
        )}
      </div>

      {/* add form */}
      {adding && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-3.5 mb-4">
          <div className="flex flex-wrap gap-2 mb-2.5">
            {picked.map((p, i) => (
              <div key={i} className="relative w-16 h-16">
                <img src={p.preview} alt="" className="w-16 h-16 rounded-lg object-cover ring-1 ring-slate-200" />
                <button onClick={() => removePicked(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-700 text-white flex items-center justify-center hover:bg-rose-600"><X size={12} /></button>
              </div>
            ))}
            {picked.length < MAX_PHOTOS && (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-16 h-16 rounded-lg border-2 border-dashed border-brand-300 text-brand-500 flex flex-col items-center justify-center hover:bg-brand-50"
              >
                <Camera size={18} />
                <span className="text-[10px] mt-0.5">ถ่ายรูป</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onPick} className="hidden" />
          </div>
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={2}
            placeholder="สภาพเครื่อง / สิ่งที่พบ…"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 resize-none"
          />
          {err && <div className="flex items-center gap-1.5 text-[12.5px] text-rose-600 mt-2"><AlertTriangle size={13} /> {err}</div>}
          <div className="flex items-center justify-end gap-2 mt-3">
            <button onClick={resetForm} disabled={saving} className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">ยกเลิก</button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} บันทึก
            </button>
          </div>
        </div>
      )}

      {/* history */}
      {!checks.length && !adding && (
        <div className="text-center py-6 text-[13px] text-slate-400">ยังไม่มีประวัติการตรวจเช็ก</div>
      )}
      <div className="space-y-3">
        {checks.map((c, i) => (
          <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-50 last:border-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[13px]">
                <span className="font-medium text-slate-700">{fmtDateTH(c.created_at)}</span>
                <span className="text-slate-400">{fmtTime(c.created_at)}</span>
              </div>
              {c.remark && <p className="text-[13px] text-slate-600 mt-0.5">{c.remark}</p>}
              {Array.isArray(c.file) && c.file.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {c.file.map((f, fi) => <PhotoTile key={fi} file={f} onClick={() => setLb({ files: c.file, index: fi })} />)}
                </div>
              )}
            </div>
            <button
              onClick={() => setConfirmEntry(c)}
              disabled={busyIdx >= 0}
              className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 shrink-0"
              title="ลบรายการนี้"
            >
              {busyIdx >= 0 ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            </button>
          </div>
        ))}
      </div>

      <ImageLightbox images={lb?.files} index={lb ? lb.index : null} onClose={() => setLb(null)} onIndex={(i) => setLb((s) => (s ? { ...s, index: i } : s))} />

      {confirmEntry && (
        <ConfirmDialog
          title="ลบรายการตรวจเช็กนี้?"
          message="รูปภาพที่แนบไว้จะถูกลบไปด้วย และกู้คืนไม่ได้"
          confirmLabel="ลบรายการ" tone="danger"
          onConfirm={() => removeCheck(confirmEntry)}
          onCancel={() => setConfirmEntry(null)}
        />
      )}
    </section>
  )
}
